import { useMemo } from 'react';
import { useModal } from 'react-native-modalfy';
import { useTranslation } from 'react-i18next';

import { modals, ModalStackParamsType } from '@modals';
import { Comment } from '@models/Comments/CommentModel';
import { ToastService } from '@services';
import { parseApiError, IS_MOBILE } from '@utils';
import type { OptionMenuOptionsType } from '../../../views/OptionsMenuPopover';

export interface IUseCommentActionMenuParams {
  item: Comment;
  onPressEdit(item: Comment): void;
  navigateToReport(): void;
}

export function useCommentActionMenu({
  item,
  onPressEdit,
  navigateToReport,
}: IUseCommentActionMenuParams) {
  const { t } = useTranslation();
  const modal = useModal<ModalStackParamsType>();

  function openRemoveModal() {
    modal.openModal(modals.ConfirmationModal, {
      title: t('common.commentItem.deleteComment'),
      subtitle: t('common.commentItem.deleteSubtitle'),
      buttons: [
        { text: t('common.commentItem.delete') },
        { text: t('common.commentItem.cancel') },
      ],
      onConfirm: () => {
        remove();
        if (IS_MOBILE) {
          // Also close options modal
          modal.closeAllModals();
        } else {
          modal.closeModal();
        }
      },
    });
  }

  async function remove() {
    try {
      await item.remove.run();

      ToastService.showSuccess(
        t('common.commentItem.successMessage'),
      );
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }

  const options = useMemo(() => {
    const values: OptionMenuOptionsType = [
      {
        label: t('common.commentItem.report'),
        icon: 'danger-light',
        onPress: navigateToReport,
      },
    ];

    if (item.user.isViewer) {
      values.push(
        {
          label: t('common.commentItem.edit'),
          icon: 'edit-light',
          onPress: () => onPressEdit(item),
        },
        {
          label: t('common.commentItem.delete'),
          icon: 'delete-light',
          onPress: openRemoveModal,
          color: '$error300',
        },
      );
    }

    return values;
  }, [item.user.isViewer]);

  return options;
}
